import { pick } from 'lodash';
import React from 'react';
import { observer } from 'mobx-react-lite';
import { Place } from 'styled-icons/material/Place';
import { HourglassHalf } from 'styled-icons/fa-solid/HourglassHalf';
import { PowerOff } from 'styled-icons/fa-solid/PowerOff';
import { Eye } from 'styled-icons/fa-regular/Eye';

import { useStore } from '@src/models';
import { Tooltip, RenderAsHTMLStyles } from '@src/features/common';
import { ShareJobModal, getJobId, formatJobToUnifiedShape } from '@src/features/job';
import { makeDeadline, makeStartDate } from '@src/features/dashboard';
import { Modal, Flex, TextBox, Box, StyledButton, BaseLink, Delimiter, BaseBox } from 'src/kit';
import { composeFullUrl, formatOffice } from 'src/helpers';
import { ROUTES } from 'src/constants';
import { JobDescriptionLabels } from '../molecules';
import { IJob, skipExternalUrl } from '../models';
import { OnboardingModal } from './OnboardingModal';

type Props = {
  isAuthorized: boolean;
  job: IJob;
  onClose: () => void;
};

export const JobInfoModal = observer(({ isAuthorized, job, onClose }: Props) => {
  const { routerStore, jobsStore } = useStore();
  const [showOnboardingModal, setShowOnboardingModal] = React.useState(false);
  const [showShareModal, setShowShareModal] = React.useState(false);

  const formattedJob = {
    ...formatJobToUnifiedShape(job),
    is_applied: jobsStore.getJob(job)?.is_applied
  };

  const [showContactInfo, setShowContactInfo] = React.useState(false);

  React.useEffect(() => {
    if (formattedJob.is_applied === undefined) {
      jobsStore.checkForJobApplication({ jobId: getJobId(job) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const jobLink = ROUTES.jobs.job.replace(':jobId', String(getJobId(job)));

  const goToJobApplicationForm = React.useCallback(() => {
    routerStore.push({
      pathname: ROUTES.jobs.applications.new.replace(':jobId', String(getJobId(job))),
      search: routerStore.location.search
    });
  }, [job, routerStore]);

  const handleApply = React.useCallback(() => {
    // logic from JobModel.ts
    if (skipExternalUrl(job)) {
      if (!isAuthorized) {
        setShowOnboardingModal(true);
      } else {
        goToJobApplicationForm();
      }
    } else {
      if (!isAuthorized) {
        goToJobApplicationForm();
      } else {
        jobsStore.simpleApplyForJob(job);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goToJobApplicationForm, isAuthorized]);

  if (showOnboardingModal) return <OnboardingModal onClose={onClose} onSubmit={goToJobApplicationForm} />;

  const officeFromJob = pick(job?.office || {}, ['lat', 'lon', 'address', 'city']);
  const office =
    job.is_anonymous && !job.candidate_is_trusted
      ? { city: job.office_city, country: job.office_country }
      : officeFromJob.city && officeFromJob.address //when job.office present show job.office, otherwise fetch from company.
      ? officeFromJob
      : pick(job.company, ['lat', 'lon', 'address', 'city']);
  const subThemes = formattedJob.ideal_profile_sub_themes || formattedJob.sub_themes;
  const focuses = formattedJob.ideal_profile_focuses || formattedJob.focuses;

  return (
    <Modal
      p="40px 40px 50px 40px"
      closeOnClickOutside={!showShareModal && !showOnboardingModal}
      onClose={onClose}
      boxShadow="shadow1"
      color="textSecondary"
      closeButtonColor="textPrimary"
    >
      {showShareModal && (
        <ShareJobModal
          shareLink={composeFullUrl(jobLink)}
          title={`Interesting job on Sainoo.com`}
          onClose={() => setShowShareModal(false)}
        />
      )}
      {(!formattedJob.is_anonymous || formattedJob.candidate_is_trusted) && (
        <Flex height={20} mb={30} alignItems="center" justifyContent="space-between">
          <TextBox textStyle="caps" height={16} color="textSecondary" letterSpacing="1.14px" fontSize={16}>
            {formattedJob.company?.name}
          </TextBox>
          {jobLink && (
            <BaseBox as={Tooltip} ml="auto" title="View job">
              <BaseLink openInNewTab href={jobLink}>
                <BaseBox as={Eye} role="button" size={22} color="coolGrey" />
              </BaseLink>
            </BaseBox>
          )}
        </Flex>
      )}
      <TextBox mb={26} textStyle="ellipsis" color="textPrimary" fontSize={20} fontWeight="bold">
        {formattedJob.position_title}
      </TextBox>
      <JobDescriptionLabels job={formattedJob} mb={15} color="textSecondary" />
      <Flex mb={30} flexWrap="nowrap" color="textSecondary" fontSize={14}>
        <Flex mr={20} alignItems="center">
          <HourglassHalf size={14} />
          <Box ml="5px">{makeDeadline(formattedJob.deadline, 'd MMM yyyy')}</Box>
        </Flex>
        {(formattedJob.start_date || formattedJob.length) && (
          <Flex alignItems="center">
            <PowerOff size={14} />
            <Box ml="5px">{makeStartDate({ startDate: formattedJob.start_date, length: formattedJob.length })}</Box>
          </Flex>
        )}
      </Flex>
      <Delimiter mb={30} />
      <BaseBox mb={10}>
        <Field label="Company size:" value={`${formattedJob.company.size} employers`} />
        <Field label="Company industry:" value={formattedJob.company.industry} />
        <Field label="Type of role:" value={formattedJob.sub_themes.map(v => v.name).join(', ')} />
        <Field label="Main focus:" value={formattedJob.focuses.map(v => v.name).join(', ')} />
      </BaseBox>
      <TextBox mb={20} fontSize={16} color="textPrimary" fontWeight="bold" textStyle="firstLetter">
        Profile of the ideal candidate
      </TextBox>
      <Field label="Experience:" value={formattedJob.experiences.join(', ')} />
      <Field label="Main focus:" value={subThemes.map(v => v.name).join(', ')} />
      <Field label="Specialties:" value={focuses.map(v => v.name).join(', ')} />

      {job.brief && (
        <Field
          flexWrap="wrap"
          textStyle="firstLetter"
          options={{ wrap: true, capitalize: false }}
          label="Brief:"
          value={
            <Box mt={15}>
              <RenderAsHTMLStyles dangerouslySetInnerHTML={{ __html: job.brief }}></RenderAsHTMLStyles>
            </Box>
          }
        />
      )}
      <Flex mb={40}>
        <Label>Office: </Label>
        <Flex alignItems="center" maxWidth="calc(100% - 160px)">
          <Place size={14} />
          <Value ml="5px" textStyle="ellipsis" flex="1">
            {formatOffice(office)}
          </Value>
        </Flex>
      </Flex>
      <Flex justifyContent="flex-end">
        <StyledButton
          onClick={() => setShowShareModal(true)}
          variant="ghost"
          size="md"
          mr={20}
          width="120px"
          lineHeight="1"
        >
          Share
        </StyledButton>
{job.is_external ? (
  <>
    {job.external_link && (
      <BaseBox mb={10}>
        <TextBox mb={10} color="textSecondary" fontSize={14}>
          You will be redirected to apply externally.
        </TextBox>
        <StyledButton
          as="a"
          href={job.external_link}
          target="_blank"
          rel="noopener noreferrer"
          variant="primary"
          size="md"
          width="120px"
        >
          Apply externally
        </StyledButton>
      </BaseBox>
    )}

    <StyledButton
      variant="ghost"
      size="md"
      width="120px"
      onClick={() => setShowContactInfo(true)}
    >
      Contact
    </StyledButton>

    {showContactInfo && (
      <Modal onClose={() => setShowContactInfo(false)} closeButtonColor="textPrimary">
        <TextBox mb={10} fontSize={16} fontWeight="bold" color="textPrimary">
          Contact Information
        </TextBox>
        <TextBox
          as="pre"
          fontFamily="monospace"
          fontSize={14}
          lineHeight="1.6"
          bg="greyLighter"
          p={20}
          whiteSpace="pre-wrap"
        >
          {job.external_contact_info || 'No contact info provided.'}
        </TextBox>
      </Modal>
    )}

    {!job.external_link && !job.external_contact_info && (
      <TextBox color="danger" fontSize={14}>
        Application instructions are missing. Please contact the company directly.
      </TextBox>
    )}
  </>
) : (
  <Tooltip
    disabled={formattedJob.is_applied}
    title={formattedJob.is_applied && 'You already applied to this position'}
  >
    <StyledButton
      onClick={handleApply}
      disabled={formattedJob.is_applied || jobsStore.isFetching}
      variant="primary"
      size="md"
      width="120px"
    >
      Apply
    </StyledButton>
  </Tooltip>
)}
      </Flex>
    </Modal>
  );
});

const Label = props => <TextBox mr={20} color="textPrimary" fontSize={16} {...{ minWidth: '140px' }} {...props} />;
const Value = props => (
  <TextBox color="textSecondary" fontWeight="normal" fontSize={16} textStyle="capitalize" {...props} />
);
const Field = ({ label, value, textStyle, ...rest }: any) =>
  value ? (
    <Flex mb={20} {...rest}>
      <Label>{label}</Label>
      <Value textStyle={textStyle}>{value}</Value>
    </Flex>
  ) : null;
